import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  MIXED_FOLDER_SORTS,
  workspaceItemsQueryShape,
} from '@bower/shared/schemas/workspaceItems.schema';
import type { WorkspaceItem, WorkspaceItemsQuery } from '@bower/shared';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import {
  queryKeys,
  STALE_TIME,
  WORKSPACE_ROOT_PARENT,
  type WorkspaceItemsListParams,
} from '../../lib/queryClient';
import { takeBootItemsPage } from '../../lib/bootItemsSlot';
import { listHashOf, noteVisibleEnd } from '../../lib/folderListRefresh';
import { apiService } from '../../services/api.service';
import { ApiResponseError, unwrapApiResponse } from '../../services/api/http-client.types';
import { listWorkspaceItems, type WorkspaceItemsPage } from '../../services/api/workspaceItems.api';
import type { WorkspaceView } from '../../stores/workspaceViewPrefsStore';
import { FOLDER_WORD } from '../../theme/entityIcons';

/**
 * The view's query half, plus the layout. The layout only decides `folders`
 * (`folderListParams`); `folders` itself is never taken from here.
 */
export type FolderItemsViewParams = Partial<
  Omit<WorkspaceItemsQuery, 'cursor' | 'limit' | 'parent' | 'folders'>
> & { readonly view?: WorkspaceView };

/** The refusals the page tells apart; anything else is `UNKNOWN`. */
export type FolderItemsErrorCode =
  | 'PROJECT_NOT_FOUND'
  | 'PROJECT_IN_TRASH'
  | 'SCOPE_TOO_LARGE'
  | 'INVALID_CURSOR'
  | 'VALIDATION_ERROR'
  | 'UNKNOWN';

const KNOWN_CODES: ReadonlyArray<FolderItemsErrorCode> = [
  'PROJECT_NOT_FOUND',
  'PROJECT_IN_TRASH',
  'SCOPE_TOO_LARGE',
  'INVALID_CURSOR',
  'VALIDATION_ERROR',
];

const DEFAULTS = workspaceItemsQueryShape.parse({});

/**
 * Rows per page: the server's maximum. Each page is a round trip, and from
 * Australia to the API region that costs ~300 ms before any work, so a list
 * scrolled 5,000 rows deep takes 50 trips, not 84 at the default 60.
 */
export const FOLDER_PAGE_SIZE = 100;

/**
 * A folder list's identity, and so its cache key: the folder plus sort, scope
 * and filters, and whether folders are mixed in. Picked field by field, so
 * the layout matters only where it changes the order: the List view under a
 * date sort mixes folders into one date order ("newest first" is the newest
 * thing of either kind); the Folder and Card views keep collections as their
 * own section above the files, so they share one cache entry.
 */
export function folderListParams(
  folderId: string | null,
  p: FolderItemsViewParams,
): WorkspaceItemsListParams {
  const sort = p.sort ?? DEFAULTS.sort;
  return {
    parent: folderId ?? WORKSPACE_ROOT_PARENT,
    scope: p.scope ?? DEFAULTS.scope,
    sort,
    order: p.order ?? DEFAULTS.order,
    folders: p.view === 'list' && MIXED_FOLDER_SORTS.includes(sort) ? 'mixed' : 'first',
    kinds: p.kinds,
    createdBy: p.createdBy,
    tags: p.tags,
    modifiedFrom: p.modifiedFrom,
    modifiedTo: p.modifiedTo,
  };
}

function toErrorCode(error: Error | null): FolderItemsErrorCode | null {
  if (!error) return null;
  const code = error instanceof ApiResponseError ? error.code : undefined;
  return KNOWN_CODES.find((known) => known === code) ?? 'UNKNOWN';
}

/**
 * One page of a folder. Page 1 also seeds the breadcrumb cache, so
 * `useFolderPath` needs no request of its own for a folder whose list (or
 * prefetch) has loaded. Shared by the list and the hover prefetch so the two
 * fill the same cache entry the same way.
 */
export async function fetchFolderPage(
  queryClient: QueryClient,
  workspaceId: string,
  params: WorkspaceItemsListParams,
  cursor: string | undefined,
  signal: AbortSignal,
): Promise<WorkspaceItemsPage> {
  // Page 1 may already be in flight from boot (`lib/bootItemsPrefetch.ts`);
  // a failed boot request falls through to the normal one.
  const boot = cursor ? null : await takeBootItemsPage(workspaceId, params, signal);
  const res =
    (boot?.success ? boot : null) ??
    (await listWorkspaceItems(apiService, { ...params, cursor, limit: FOLDER_PAGE_SIZE }, signal));
  const page = unwrapApiResponse(res, `Could not load this ${FOLDER_WORD}`);
  if (!cursor && params.parent !== WORKSPACE_ROOT_PARENT && page.folderPath) {
    queryClient.setQueryData(queryKeys.folderPath(workspaceId, params.parent), page.folderPath);
  }
  return page;
}

/**
 * One folder's items (folders first, then files, or mixed in one date order
 * in the List view: `folderListParams`), paged by the opaque cursor.
 * Follows `useInfinitePaginated`'s conventions (`flatItems`, page-1
 * `totalCount`) without wrapping it, for two reasons: the page carries page-1
 * meta beyond `PaginatedResponse`, and the placeholder must be folder-aware.
 *
 * Placeholder: a sort, scope or filter change keeps the current rows on screen
 * until the new page lands (`isPlaceholderData` is true meanwhile). A folder
 * change does NOT: showing folder A's rows under folder B's breadcrumb would
 * be wrong, so B starts pending. Nor does a workspace change (the root's
 * parent is the same literal in every workspace).
 */
export function useFolderItems(folderId: string | null, viewParams: FolderItemsViewParams) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const params = folderListParams(folderId, viewParams);
  const queryKey = queryKeys.workspaceItems.list(workspaceId ?? '', params);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      fetchFolderPage(queryClient, workspaceId ?? '', params, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: WorkspaceItemsPage) =>
      last.hasMore ? (last.nextCursor ?? undefined) : undefined,
    enabled: !!workspaceId,
    staleTime: STALE_TIME.DEFAULT,
    // Same list = same workspace AND same folder. The root's parent segment
    // is the same literal in every workspace, so the workspace must match too.
    placeholderData: (previous, previousQuery) => {
      const sameList =
        previousQuery?.queryKey[1].workspaceId === workspaceId &&
        previousQuery.queryKey[2].parent === params.parent;
      return sameList ? previous : undefined;
    },
  });

  // Stable per list, so the views' effects re-run only when their window moves.
  const listHash = listHashOf(queryKey);
  const onVisibleEnd = useCallback(
    (lastIndex: number) => noteVisibleEnd(listHash, lastIndex),
    [listHash],
  );

  const pages = query.data?.pages;
  const flatItems = useMemo<WorkspaceItem[]>(() => pages?.flatMap((p) => p.items) ?? [], [pages]);
  const first = pages?.[0];

  const { fetchNextPage } = query;
  // Joins a refresh in flight instead of cancelling it (TanStack's default),
  // which would drop the remote change it is fetching. A refresh trims the
  // list (`folderListRefresh`), and that re-arms the views' end-reached, so
  // this ask arrives mid-refresh routinely, not by chance.
  const fetchNextPageJoining = useCallback(
    (options?: Parameters<typeof fetchNextPage>[0]) =>
      fetchNextPage({ cancelRefetch: false, ...options }),
    [fetchNextPage],
  );

  return {
    ...query,
    fetchNextPage: fetchNextPageJoining,
    flatItems,
    /** Page 1 only (the server sends it once); null until it arrives. */
    totalCount: first?.totalCount ?? null,
    /** Page 1's split of `totalCount`; null until it arrives. */
    folderCount: first?.folderCount ?? null,
    fileCount: first?.fileCount ?? null,
    folderPath: first?.folderPath,
    hiddenByFilters: first?.hiddenByFilters,
    errorCode: toErrorCode(query.error),
    /** The views report their last rendered row, so a refresh stops there (`folderListRefresh`). */
    onVisibleEnd,
  };
}
